import { supabase } from "@/integrations/supabase/client";

export type SettingsKey = "store" | "branding" | "operation" | "preferences";

export interface StoreSettings {
  name?: string;
  email?: string;
  phone?: string;
  document?: string; // CNPJ/CPF
  institutional_text?: string;
  origin_address?: {
    zip_code?: string;
    street?: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
  };
}

export interface BrandingSettings {
  display_name?: string;
  logo_url?: string;
  favicon_url?: string;
  brand_image_url?: string;
  primary_color?: string;
  accent_color?: string;
}

export interface OperationSettings {
  exchange_policy?: string;
  shipping_text?: string;
  default_message?: string;
  free_shipping_threshold?: number;
  checkout_notice?: string;
}

export interface PreferencesSettings {
  default_locale?: string;
  default_currency?: string;
  enable_wishlist?: boolean;
  enable_reviews?: boolean;
}

export type SettingsMap = {
  store: StoreSettings;
  branding: BrandingSettings;
  operation: OperationSettings;
  preferences: PreferencesSettings;
};

export async function getSetting<K extends SettingsKey>(key: K): Promise<SettingsMap[K]> {
  const { data, error } = await supabase
    .from("app_settings")
    .select("value")
    .eq("key", key)
    .maybeSingle();
  if (error) throw error;
  return ((data?.value as unknown) ?? {}) as SettingsMap[K];
}

export async function getAllSettings(): Promise<Partial<SettingsMap>> {
  const { data, error } = await supabase
    .from("app_settings")
    .select("key, value")
    .in("key", ["store", "branding", "operation", "preferences"]);
  if (error) throw error;
  const out: Partial<SettingsMap> = {};
  (data ?? []).forEach((row) => {
    (out as any)[row.key] = row.value ?? {};
  });
  return out;
}

export async function updateSetting<K extends SettingsKey>(
  key: K,
  value: SettingsMap[K],
): Promise<void> {
  // Garante upsert por key (já existe seed, mas preserva idempotência)
  const { error } = await supabase
    .from("app_settings")
    .upsert({ key, value: value as any }, { onConflict: "key" });
  if (error) throw error;
}

export interface AdminUserRow {
  user_id: string;
  email: string | null;
  role: string;
  created_at: string;
}

export async function listAdminUsers(): Promise<AdminUserRow[]> {
  const { data, error } = await supabase.rpc("list_admin_users");
  if (error) throw error;
  return (data ?? []) as AdminUserRow[];
}

// ===== Branding upload =====
const BRANDING_BUCKET = "content-images";

export async function uploadBrandingImage(
  file: File,
  kind: "logo" | "favicon" | "brand",
): Promise<string> {
  const ext = file.name.split(".").pop()?.toLowerCase() || "png";
  const path = `branding/${kind}-${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from(BRANDING_BUCKET)
    .upload(path, file, { cacheControl: "3600", upsert: false });
  if (error) throw error;
  const { data } = supabase.storage.from(BRANDING_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

// ===== Integrações (status visual) =====
export type IntegrationStatus = "configured" | "pending" | "sandbox" | "production";

export interface IntegrationInfo {
  id: string;
  name: string;
  description: string;
  status: IntegrationStatus;
  environment?: "sandbox" | "production";
  webhook_status?: "configured" | "pending";
  docs_hint?: string;
}
